import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import apiClient from '@/services/api-client';
import { apiConfig } from '@/config/api';

export default function LoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordError, setForgotPasswordError] = useState('');
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false);
  const [isSendingForgotPassword, setIsSendingForgotPassword] = useState(false);
  const { login, isAuthenticated, user } = useAuth();

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      // Reload page to ensure Header gets updated user data with roles
      window.location.href = '/loans';
    }
  }, [isAuthenticated, user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const success = await login(username, password);
      if (success) {
        // Reload page to ensure Header gets updated user data with roles
        window.location.href = '/loans';
      } else {
        setError('Invalid email or password');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('An error occurred during login');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSendingForgotPassword(true);
    setForgotPasswordError('');
    setForgotPasswordSuccess(false);

    try {
      await apiClient.post(apiConfig.endpoints.auth.forgotPassword, {
        email: forgotPasswordEmail,
      });
      setForgotPasswordSuccess(true);
      setForgotPasswordEmail('');
    } catch (err: any) {
      console.error('Forgot password error:', err);
      setForgotPasswordError(
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        'An error occurred. Please try again.'
      );
    } finally {
      setIsSendingForgotPassword(false);
    }
  };

  const handleBackToLogin = () => {
    setShowForgotPassword(false);
    setForgotPasswordEmail('');
    setForgotPasswordError('');
    setForgotPasswordSuccess(false);
  };

  return (
    <div className="auth-container" data-test-id="login-page-container">
      <Card
        className="card absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 min-w-md"
        data-test-id="login-card"
      >
        <CardHeader data-test-id="login-card-header">
          <CardTitle data-test-id="login-title">
            {showForgotPassword ? 'Forgot Password' : 'Login'}
          </CardTitle>
        </CardHeader>
        <CardContent data-test-id="login-card-content">
          {showForgotPassword ? (
            <form
              onSubmit={handleForgotPassword}
              className="form-group space-y-4"
              data-test-id="forgot-password-form"
            >
              <div data-test-id="forgot-password-email-wrapper">
                <Label
                  htmlFor="forgot-password-email"
                  className="form-label"
                  data-test-id="forgot-password-email-label"
                >
                  Email
                </Label>
                <Input
                  id="forgot-password-email"
                  type="email"
                  value={forgotPasswordEmail}
                  onChange={(e) => setForgotPasswordEmail(e.target.value)}
                  autoComplete="email"
                  required
                  className="input-full"
                  disabled={isSendingForgotPassword}
                  data-test-id="forgot-password-email-input"
                />
              </div>
              {forgotPasswordError && (
                <div className="error-text" data-test-id="forgot-password-error">
                  {forgotPasswordError}
                </div>
              )}
              {forgotPasswordSuccess && (
                <div className="text-green-600 text-sm bg-green-50 p-3 rounded" data-test-id="forgot-password-success">
                  Email with temporary password has been sent
                </div>
              )}
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleBackToLogin}
                  disabled={isSendingForgotPassword}
                  className="flex-1"
                  data-test-id="back-to-login-button"
                >
                  Back to Login
                </Button>
                <Button
                  type="submit"
                  className="btn-primary flex-1"
                  disabled={isSendingForgotPassword}
                  data-test-id="forgot-password-submit-button"
                >
                  {isSendingForgotPassword ? 'Sending...' : 'Send'}
                </Button>
              </div>
            </form>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="form-group space-y-4"
              data-test-id="login-form"
            >
              <div data-test-id="username-field-wrapper">
                <Label
                  htmlFor="email"
                  className="form-label"
                  data-test-id="username-label"
                >
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="email"
                  required
                  className="input-full"
                  disabled={isLoading}
                  data-test-id="username-input"
                />
              </div>
              <div data-test-id="password-field-wrapper">
                <Label
                  htmlFor="password"
                  className="form-label"
                  data-test-id="password-label"
                >
                  Password
                </Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                  className="input-full"
                  disabled={isLoading}
                  data-test-id="password-input"
                />
              </div>
              {error && (
                <div className="error-text" data-test-id="login-error">
                  {error}
                </div>
              )}
              <Button
                type="submit"
                className="btn-primary input-full"
                disabled={isLoading}
                data-test-id="login-submit-button"
              >
                {isLoading ? 'Logging in...' : 'Login'}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setShowForgotPassword(true)}
                className="w-full text-sm text-gray-600 hover:text-gray-900"
                data-test-id="forgot-password-button"
              >
                Forgot Password?
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
